import { useTranslation } from "react-i18next";
import { useEffect, useState, type ComponentType } from "react";
import { BookOpen, FileText, Loader2 } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../../auth/keycloak.js";
import type { FacilityTreeNode } from "../facility-tree.js";
import { SearchField } from "@/components/ui/search-field";
import { Button } from "@/components/ui/button";
import { PageBackRow } from "@/components/PageBackRow";
import { FacilityIcon } from "@/components/icons";
import { CountPill } from "../CountPill.js";
import { FacilityRailList } from "./FacilityRailList.js";
import { ManualReferenceElement } from "../ManualReferenceElement.js";
import { useManualSearch } from "../useManualSearch.js";
import type { KebabProps } from "./useDrilldownActions.js";

type SearchTab = "facilities" | "manuals" | "descriptions";

/** A leading `#` is the legacy shortcut for a DESCRIPTION search — it opens the Descriptions tab and never reaches the
 *  actual query term (`ARLMS_FacilitySelectionSearch.js`). */
const stripHashPrefix = (q: string): string => q.replace(/^#/, "").trim();

/**
 * The global search view (FN4b — the legacy "Suche"): opened from the filter bar's search box (`arc_search`). A header
 * (a search field + tabs + "End search") over a flat, path-labelled result list. Labels are English for now; i18n
 * (Paraglide) lands later.
 *
 * Three tabs (FN4b-2):
 * - **Facilities** — client-side over the already-permission-filtered `activeFlat` tree ({@link FacilityDrilldown}
 *   passes the A-Z matches as `results`); reuses the shared flat-list renderer ({@link FacilityRailList}).
 * - **Manuals** (by NAME) — the permission-filtered `GET /manuals/search` endpoint (the central #107 direct-view fix);
 *   the card shows NO description (the name is what matched).
 * - **Descriptions** (by manual DESCRIPTION, `field=description`) — the card ADDITIONALLY shows the description with the
 *   matched term highlighted. A `#`-prefixed deep-link opens this tab.
 *
 * Each hit is a {@link ManualReferenceElement} (its "Öffnen" jumps to the facility's manuals view — Option A). Opening a manual keeps the search + tab UNDER it, so the manuals "Back" returns here on the same chip;
 * drilling (or End-search) exits. The active tab is URL-backed (`arc_stab`) so it survives that round-trip.
 */
export function FacilitySearchView({
  query,
  results,
  session,
  searchTab,
  onSearchTabChange,
  resolveFacilityPath,
  getPathLabel,
  getKebab,
  favorites,
  onToggleFavorite,
  onDrill,
  onOpenManuals,
  onSearch,
  onClose,
}: {
  /** The active `arc_search` query (seeds the field + the Facilities result set). */
  query: string;
  /** Facility matches — facilities whose name matches `query`, resolved over the active tree, A-Z. */
  results: readonly FacilityTreeNode[];
  /** Auth session — drives the manual-search query (gated to the caller's realm/sub). */
  session: AuthSession | null;
  /** The URL-backed active tab (`arc_stab`; null ⇒ derive a default from the `#`-prefix). */
  searchTab: SearchTab | null;
  /** Persist the active tab (replaceState) so it survives the open-manual → Back round-trip. */
  onSearchTabChange: (tab: SearchTab) => void;
  /** Resolve a facility id to its "Root / Parent / Facility" label from the loaded tree (null ⇒ use the server name). */
  resolveFacilityPath: (facilityId: number) => string | null;
  getPathLabel: (node: FacilityTreeNode) => string;
  getKebab: (node: FacilityNode) => KebabProps | null;
  favorites: ReadonlySet<number>;
  onToggleFavorite: (id: number) => void;
  /** Drill into a facility result (exits search — the nav hook clears `arc_search`). */
  onDrill: (id: number) => void;
  /** Open a result's manuals — a facility card, or a manual hit's parent facility (exits search). */
  onOpenManuals: (id: number) => void;
  /** Re-run the search for a new query (field submit). */
  onSearch: (query: string) => void;
  /** "End search" — leave the search view, back to the tree. */
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // The field is seeded from the URL query but edits locally until submitted (Enter) — so typing doesn't spam history.
  const [draft, setDraft] = useState(query);
  // The active tab is URL-backed (`arc_stab`) so it survives an open-manual → Back round-trip. When unset, derive the
  // default: a `#`-prefixed deep-link opens Descriptions (legacy shortcut), otherwise Facilities. Clicking a chip writes it.
  const tab: SearchTab = searchTab ?? (query.startsWith("#") ? "descriptions" : "facilities");
  const setTab = onSearchTabChange;
  // Re-sync the field when the URL query changes underneath it — browser Back/Forward moves `arc_search`, and the field
  // must follow the results it's showing (panel #1). `draft` is otherwise local (typing between submits stays local).
  useEffect(() => setDraft(query), [query]);
  // Submit: a non-empty query re-searches; an EMPTY query LEAVES search (dropping `arc_search` — panel #2). Otherwise a
  // cleared field would strand stale results with no exit but the End-search button.
  const submit = () => {
    const q = draft.trim();
    if (q) onSearch(q);
    else onClose();
  };

  // Manual search — Manuals (by name) or Descriptions (by description). Gated to a manual tab so browsing Facilities
  // fires no request. The term drops a leading `#` (the description shortcut). The matched term is highlighted in the
  // card; the description BLOCK is shown only on the Descriptions tab (the name is what matched on the Manuals tab).
  const manualTabActive = tab === "manuals" || tab === "descriptions";
  const manualTerm = stripHashPrefix(query);
  const manualField = tab === "descriptions" ? "description" : "name";
  const manualSearch = useManualSearch(session, manualTerm, manualField, manualTabActive);
  const manualCount = manualTabActive && manualSearch.isSuccess ? manualSearch.data.length : undefined;

  const tab_ = (key: SearchTab, label: string, Icon: ComponentType<{ className?: string }>, count?: number) => {
    const active = key === tab;
    return (
      <Button
        variant={active ? "default" : "secondary"}
        size="sm"
        className="rounded-full"
        aria-pressed={active}
        onPress={() => setTab(key)}
      >
        <Icon className="size-3.5" aria-hidden="true" />
        {label}
        {count !== undefined ? <CountPill n={count} /> : null}
      </Button>
    );
  };

  return (
    <section aria-label={t("facilitySelection.search.region")} className="flex min-h-0 flex-1 flex-col">
      {/* Header — search field + tabs + End search, aligned to the same centered column as the results. */}
      <div className="border-border border-b py-4">
        <div className="page-column flex flex-col gap-4">
          <PageBackRow label={t("facilitySelection.search.endSearch")} onBack={onClose} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 id="facilities-heading" className="text-xl font-semibold">
              {t("facilitySelection.search.results")}
            </h1>
            <SearchField
              autoFocus
              label={t("facilitySelection.search.label")}
              placeholder={t("facilitySelection.search.placeholder")}
              value={draft}
              onChange={setDraft}
              clearLabel={t("facilitySelection.clearSearch")}
              // Clearing the search removes it → back to the tree (an empty search view is a dead-end — panel #2).
              onClear={onClose}
              onSubmit={() => submit()}
              className="w-full sm:w-72"
            />
          </div>

          {/* Tabs — Facilities (client) · Manuals (name) · Descriptions (description). */}
          <div role="group" aria-label={t("facilitySelection.search.scope")} className="flex flex-wrap items-center gap-2">
            {tab_("facilities", t("facilitySelection.search.scopeFacilities"), FacilityIcon, results.length)}
            {tab_("manuals", t("facilitySelection.search.scopeManuals"), BookOpen, tab === "manuals" ? manualCount : undefined)}
            {tab_("descriptions", t("facilitySelection.search.scopeDescriptions"), FileText, tab === "descriptions" ? manualCount : undefined)}
          </div>
        </div>
      </div>

      {/* Results — the facilities tab reuses the shared flat-list renderer; the manual tabs render search cards. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="page-column py-4 sm:py-6">
          {tab === "facilities" ? (
            <FacilityRailList
              key={query}
              nodes={results}
              ariaLabel={t("facilitySelection.search.facilityResults", { query })}
              emptyLine={t("facilitySelection.search.noFacilityMatch", { query })}
              getKebab={getKebab}
              favorites={favorites}
              onToggleFavorite={onToggleFavorite}
              onDrill={onDrill}
              onOpenManuals={onOpenManuals}
              getPathLabel={getPathLabel}
            />
          ) : manualSearch.isPending ? (
            <p className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
              <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("facilitySelection.search.searching")}
            </p>
          ) : manualSearch.isError ? (
            <div className="text-muted-foreground flex flex-col items-start gap-2 py-8 text-sm">
              <span>{t("facilitySelection.search.searchError")}</span>
              <Button variant="outline" size="sm" onPress={() => void manualSearch.refetch()} isDisabled={manualSearch.isFetching}>
                {t("actions.tryAgain")}
              </Button>
            </div>
          ) : manualSearch.data.length === 0 ? (
            <p className="text-muted-foreground py-8 text-sm">
              {tab === "descriptions"
                ? t("facilitySelection.search.noDescriptionMatch", { query: manualTerm })
                : t("facilitySelection.search.noManualMatch", { query: manualTerm })}
            </p>
          ) : (
            <ul aria-label={t("facilitySelection.search.manualResults", { query: manualTerm })} className="m-0 flex list-none flex-col gap-2.5 p-0">
              {manualSearch.data.map((result) => (
                <li key={result.id}>
                  <ManualReferenceElement
                    manual={result}
                    path={resolveFacilityPath(result.facilityId) ?? result.facilityName}
                    query={manualTerm}
                    description={tab === "descriptions" ? result.descriptionText : null}
                    // Wrapped: the element also passes the manual id, which must NOT reach `openManuals`' `replace` flag.
                    onOpen={(facilityId) => onOpenManuals(facilityId)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
